import { GraphQLClient, type Variables } from 'graphql-request';

import { ensureFreshToken, refreshAndRetry } from '../features/auth/tokenRefresh';

const HASURA_ENDPOINT = import.meta.env.VITE_HASURA_ENDPOINT ?? 'http://localhost:8080/v1/graphql';

/**
 * Per-call Hasura GraphQL client carrying the user's *current* access
 * token. Two layers of refresh:
 *
 *  1. Proactive — `ensureFreshToken()` checks the JWT's `exp` claim and
 *     refreshes if within 60 s of expiry, BEFORE the request goes out.
 *     Avoids the common-case 401-retry round trip.
 *
 *  2. Reactive — if the request still 401s (clock skew, revoked token),
 *     `refreshAndRetry` runs the refresh + retries once with a fresh
 *     token. If THAT fails too, the error bubbles up and the user is
 *     bounced to the login route by the App-level guard.
 */
function clientFor(token: string | null): GraphQLClient {
  return new GraphQLClient(HASURA_ENDPOINT, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function gql<T, V extends Variables = Variables>(
  query: string,
  variables?: V,
): Promise<T> {
  const token = await ensureFreshToken();
  try {
    return await clientFor(token).request<T>(query, variables ?? ({} as V));
  } catch (err) {
    return refreshAndRetry(err, (newToken) =>
      clientFor(newToken).request<T>(query, variables ?? ({} as V)),
    );
  }
}

